import { Link } from 'react-router-dom';
import { formatPrice } from '../../lib/utils';
import type { Product } from '../../types';
import { productPath } from '../../lib/productPath';
import ProductImage from './ProductImage';
import ProductQuantityControl from './ProductQuantityControl';

interface ProductCardProps {
  product: Product;
  preview?: boolean;
  /** Renders a denser card for the chat panel, where space is ~150px wide. */
  compact?: boolean;
  /** Called when the card is activated, so the chat can close before navigating. */
  onNavigate?: () => void;
  className?: string;
}

export default function ProductCard({ product, preview = false, compact = false, onNavigate, className = '' }: ProductCardProps) {
  if (compact) {
    return (
      <article data-testid="product-card-compact" className={`group flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-glass-line/15 bg-raised/70 ${className}`}>
        <Link to={productPath(product)} onClick={onNavigate} tabIndex={preview ? -1 : undefined} className={`flex min-h-0 flex-1 flex-col ${preview ? 'pointer-events-none' : ''}`}>
          <div className="relative shrink-0">
            <ProductImage product={product} naturalAspect />
          </div>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-1 px-2 py-2">
            <h3 className="line-clamp-2 break-words text-[13px] font-semibold leading-snug text-ink" title={product.name}>{product.name}</h3>
            <div className="mt-auto flex min-w-0 flex-wrap items-baseline gap-x-1.5">
              <span className="text-sm font-bold text-warning/80">{formatPrice(product.price)}</span>
              {product.originalPrice && <span className="text-[11px] text-subtle line-through">{formatPrice(product.originalPrice)}</span>}
            </div>
          </div>
        </Link>

        {!preview && <ProductQuantityControl product={product} compact className="mt-2 px-2 pb-2" />}
      </article>
    );
  }

  return (
    <article data-testid="product-card" className={`group h-full flex flex-col rounded-2xl border border-glass-line/15 bg-gradient-to-b from-raised/70 via-surface/80 to-canvas/85 px-1.5 pt-0 pb-1.5 transition-all duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-lg hover:shadow-brand/10 ${className}`}>
      <Link to={productPath(product)} onClick={onNavigate} tabIndex={preview ? -1 : undefined} className={`flex min-h-0 flex-1 flex-col ${preview ? 'pointer-events-none' : ''}`}>
        <div className="relative">
          <ProductImage product={product} naturalAspect />
        </div>

        <div className="min-h-0 flex-1" />

        <div className="px-1 text-left mt-2">
          <div className="product-title-plate flex min-h-14 items-center rounded-xl border border-glass-line/15 bg-surface/90 px-2 py-1.5 shadow-sm shadow-canvas/30 backdrop-blur-md">
            <h3 className="font-display font-semibold leading-snug text-ink text-[15px] xl:text-base">{product.name}</h3>
          </div>
          <div className="flex min-h-[2.25rem] items-end justify-end gap-2 pt-2">
            <span className="font-display font-bold text-warning/70 text-xl xl:text-2xl">{formatPrice(product.price)}</span>
            {product.originalPrice && <span className="text-sm text-subtle line-through">{formatPrice(product.originalPrice)}</span>}
          </div>
        </div>
      </Link>

      {!preview && <ProductQuantityControl product={product} className="mt-3" />}
    </article>
  );
}
